/* System defined core library */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Observer, Subscription, forkJoin } from 'rxjs';

/* User defined library/components */
import { BootstrapParams } from '../../../bootstrap-params';
import { HttpService } from '../../core/http.service';
import { AppointmentService } from '../../scheduling/shared/services/appointment/appointment.service';
import { MetricApiService } from '../../scheduling/shared/services/metric-api.service';
import { OrchestrationApiService } from '../../scheduling/shared/services/orchestration-api.service';
import { RegistrationApiService } from '../../scheduling/shared/services/registration-api.service';
import { SchedulingApiService } from '../../scheduling/shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../scheduling/shared/services/selected-exam-detail.service';
import { DateTimeService } from '../../shared/services/date-time.service';

/* User defined models */
import { DynamicFormBase } from '../models/common/dynamic-form-base.model';
import { LpiData } from '../models/common/lpiData.model';
import { Dictionary } from '../models/common/shared.model';
import { ProSchedulerTextbox } from '../models/common/textbox.model';
import { AppointmentDetail } from '../models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../models/scheduling/appointmentSlot.model';
import { Candidate } from '../models/scheduling/candidate.model';
import { Client } from '../models/scheduling/client.model';
import { Country } from '../models/scheduling/country.model';
import { Eligibility, EligibilityDetail } from '../models/scheduling/eligibility.model';
import { Exam } from '../models/scheduling/exam.model';
import { GetFlowsResponseItemData } from '../models/scheduling/flows.model';
import { Payment } from '../models/scheduling/payment.model';
import { Program } from '../models/scheduling/program.model';
import { SiteSlot } from '../models/scheduling/siteSlot.model';

/* User defined enums/constants */
import * as constants from '../constants/scheduling.constant';
import { Fee, LogLevel } from '../enums/scheduling.enum';

@Injectable()

// URL to test
// http://localhost:32971?path=scheduling&clientId=NASD&prg=2

export class DeepLinkingService {
/* User defined private/public variables */
  private client: any;
  private eligibilityDetails: EligibilityDetail[];
  private exam: any;
  private exams: Exam[];
  private isEligibilityRequired: boolean;
  private lpiCandidate: Candidate;
  private lpiData: LpiData;
  private overLapEndDate: any;
  private overLapStartDate: any;
  private program: any;
  private programCodes: string[] = [];
  private programs: Program[] = [];
  private promiseChain: Array<string> = [];
  private selectedAppointmentDetails: AppointmentDetail[];
  private sessionLoadError: boolean = false;
  public  TempAppointmentDetails: AppointmentDetail[] = [];
  /* Service subscriptions */
  lpiCandidate$: Subscription;
  lpiData$: Subscription;
  overLapEndDate$: Subscription;
  overLapStartDate$: Subscription;
  selectedAppointmentDetails$: Subscription;

  constructor(
    private appointmentService: AppointmentService,
    private bootstrapParams: BootstrapParams,
    private dateTimeService: DateTimeService,
    private httpService: HttpService,
    private orchestrationApiService: OrchestrationApiService,
    private registrationApiService: RegistrationApiService,
    private router: Router,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private metricApiService: MetricApiService
  ) {
    this.lpiCandidate$ = selectedExamDetailService.lpiCandidate.subscribe(
      lpiCandidate => {
        this.lpiCandidate = lpiCandidate;
      });

    this.lpiData$ = selectedExamDetailService.lpiData.subscribe(
      lpiData => {
        this.lpiData = lpiData;
      });

    this.overLapEndDate$ = selectedExamDetailService.overlapsToDate.subscribe(
      endDate => {
        this.overLapEndDate = endDate;
      });

    this.overLapStartDate$ = selectedExamDetailService.overlapsFromDate.subscribe(
      startDate => {
        this.overLapStartDate = startDate;
      });

    this.selectedAppointmentDetails$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointmentDetails => {
        this.selectedAppointmentDetails = selectedAppointmentDetails;
      });
  }

  /* Indicates the parameters required for a particular workflow */
  pathParameters = {
    'schd': ['client', 'prg', 'fname', 'lname', 'eligid', 'nopri', 'exam', 'termurl', 'pl', 'noconfirm', 'candDemo', 'cntry', 'st', 'culture'],
    'confirm': ['confnum', 'lname', 'pl', 'termurl', 'noconfirm', 'cntry', 'st', 'prg', 'culture', 'client'],
    'reschdcancel': ['confnum', 'lname', 'pl', 'termurl', 'noconfirm', 'cntry', 'st', 'prg', 'culture', 'client'],
    'reschd': ['confnum', 'lname', 'pl', 'termurl', 'noconfirm', 'cntry', 'st', 'prg', 'culture', 'client'],
    'cancel': ['confnum', 'lname', 'pl', 'termurl', 'noconfirm', 'cntry', 'st', 'prg', 'culture', 'client'],
    'seatavail': ['prg', 'exam', 'pl', 'termurl', 'cntry', 'st', 'prg', 'culture', 'client'],
    'proctorsch': ['client', 'prg', 'fname', 'lname', 'eligid', 'nopri', 'exam', 'termurl', 'pl', 'noconfirm', 'candDemo', 'cntry', 'st', 'culture'],
    'proctorresch': ['confnum', 'lname', 'pl', 'termurl', 'noconfirm', 'cntry', 'st', 'prg', 'culture', 'client'],
    'proctorseatavail': ['prg', 'exam', 'pl', 'termurl', 'cntry', 'st', 'prg', 'culture', 'client'],
    'completeappt': ['session']
  };

  /*
    A map containing the functions required
    for a required parameter
  */
  workflow = {
    prg: ['getProgram'],
    fname: ['validateEligibility'],
    eligid: ['validateEligibility'],
    confnum: ['validateConfNumber'],
    nopri: ['skipPrivacyPage'],
    exam: ['getExam'],
    termurl: ['terminationUrl'],
    pl: ['setPathLock'],
    candDemo: ['setCandidateDemographics'],
    noconfirm: ['skipConfirmationPage'],
    cntry: ['setCountryCode'],
    st: ['setStateCode'],
    culture: ['setCulture'],
    client: ['validateClient'],
    session: ['getSessionData']
  };

  path = {
    'schd': 'getSchedulePath',
    'confirm': 'getConfirmPath',
    'reschdcancel': 'getReschdCancelPath',
    'reschd': 'getReschdPath',
    'cancel': 'getCancelPath',
    'seatavail': 'getSeatAvail',
    'proctorsch': 'getProctorSchedPath',
    'proctorresch': 'getProctorReschedPath',
    'proctorseatavail': 'getProctorSeatAvail',
    'completeappt': 'getApptConfirmationPath'
  };

  /* helper function to get Client information using clientId */
  getClient(clientHref: any) {
    return new Promise((resolve, reject) => {
      this.getClientDetails(clientHref.href).subscribe((client: Client[]) => {
        this.client = client;
        this.setClient();
        return resolve(true);
      })
    });
  }

  /* helper function to set client in the service */
  setClient() {
    this.selectedExamDetailService.setClient(this.client);
    this.selectedExamDetailService.doesClientExist = true;
    if (this.programCodes.length === 1) {
      this.selectedExamDetailService.setProgram(this.program);
    }
    else {
      this.selectedExamDetailService.setPrograms(this.programs);
    }
  }

  validateClient(params: any) {
    return new Promise((resolve, reject) => {
      if (!params.client) return resolve(true);

      this.selectedExamDetailService.clientCode = params.client;

      this.schedulingApiService.getClient(params.client).subscribe((client: any) => {
        this.selectedExamDetailService.setClient(client);
        this.client = client;
        return resolve(true);
      })

    })
  }

  /* helper function to get program information using programId */
  getProgram(params: any) {
    return new Promise((resolve, reject) => {
      // debounce(function () {
      if (!params.prg) return resolve(true);
      // split on receiving multiple prgms
      this.programCodes = params.prg.split(',');
      if (this.programCodes.length === 1) {
        this.selectedExamDetailService.programCode = params.prg;
        this.schedulingApiService.getProgram(params.prg).subscribe((program: any) => {
          this.program = program.length ? program[0] : null;
          this.setPathLock(params);
          const clientHref = this.program.client;

          this.getExam(params);

          return this.getClient(clientHref).then((value: boolean) => {
            if (params.path === constants.Path.Schedule || params.path === constants.Path.ProctorSchedule) {
              if (value) {
                return this.checkEligibility().then((val: boolean) => {
                  if (val && this.program && this.checkEligibility()) {
                    return resolve(true);
                  }
                  return resolve(false);
                });
              }
            }
            return resolve(true);
          });
        });
      }

      else {
        let prgPromise: Array<object> = [];
        let clientIds: string[] = [];
        let clientHref: any;
        this.programCodes.forEach((prg: string) => {
          //create promise and push in promise array for each prg
          prgPromise.push(new Promise((resolve, reject) => {
            this.schedulingApiService.getProgram(prg).subscribe((program: any) => {
              // TODO: Find why this is getting called twice on seatavail - KJC
              if (!this.programs.some(prog => {
                return prog.id === program[0].id;
              })) {
                this.programs.push(program[0]);
              }
              clientHref = program[0].client;
              clientIds.push(clientHref.id);
              resolve(true);
            });
          }));
        });
        // resolve all promises before verifying clientIds
        return Promise.all(prgPromise).then(() => {
          if (clientIds.every(clientId => (clientId === clientIds[0]))) {
            return this.getClient(clientHref).then((value: boolean) => {
              if (value) {
                return resolve(true);
              }
              return resolve(false);
            });
          }
        });

      }
    });
  }

  /**
   * get exam using code
   * @param params
   */
  getExam(params: any) {
    return new Promise((resolve, reject) => {
      if (!params.exam || (params.eligid && params.lname)) {
        return resolve(true);
      }
      const examCode = this.getExamCodeFromParam(params.exam);

      this.selectedExamDetailService.lpiExamCode = examCode;

      const programId = this.program ? this.program.id : null;
      if (programId) {
        this.schedulingApiService.getExam(examCode, programId).subscribe(
          (exam: Exam[]) => {
            this.exam = exam.length ? exam[0] : null;
            this.setExam();
            return resolve(true);
          }
        );
      }
      return resolve(true);
    });
  }

  getExamCodeFromParam(examParam: any): string {
    let exam = examParam.toString().replace('+', ' ');
    // handle if exam code is appended by program code
    const hasWhiteSpace = exam.indexOf(' ') > 0;
    const examParams = exam.split(' ');

    return hasWhiteSpace ? examParams[examParams.length - 1] : exam;
  }

  setExam() {
    this.selectedExamDetailService.setExam(this.exam);
    this.lpiData.exam = this.exam;

    if (!!this.exam) {

      this.selectedExamDetailService.setCountry(null);
      this.selectedExamDetailService.setRegion(null);
      this.selectedExamDetailService.setState(null);

      this.setAppointmentDetails([this.exam]);

      if (this.client && this.program && this.exam) {
        this.orchestrationApiService.getFlows(this.client.code, this.program.code, this.exam.code).subscribe((response: any) => {
          if (response.length > 0) {

            response = response.filter(function (el: any) {
              return el.name == "Candidate Selectable";
            });
            // check if testingAccommodations have length
            this.selectedExamDetailService.showCandidateSelectableView =
              response.length ? !!response[0].data.length : false;
          }
        });
      }
    }
  }

  /**
   * helper function to validate eligibility and
   * @param params
   */
  validateEligibility(params: any) {

    // This is temporary changes and need to discuss how to handle this.
    // Making a seperate call for flows is taking too much time here.
    let eligibility: Eligibility = new Eligibility();

    return new Promise((resolve, reject) => {
      if (['eligid', 'prg'].every(value => this.isClosed(params[value]))) {

        var lname = this.bootstrapParams.candidateLastName || params['lname'];
        eligibility.eligibilityDetails = new Dictionary();
        this.lpiData.eligibility = new Eligibility();
        this.lpiData.eligibility.eligibilityDetails = new Dictionary();
        eligibility.eligibilityDetails.add("ELIGIBILITY_NUMBER", params['eligid']);
        if (lname && lname.length) {
          eligibility.eligibilityDetails.add("LAST_NAME", lname);
        } else {
          eligibility.eligibilityDetails.add("LAST_NAME", null);
        }

        if (params['fname']) {
          eligibility.eligibilityDetails.add("FIRST_NAME", params['fname']);
        }

        if (!!eligibility) {
          eligibility.eligibilityDetails._keys.forEach((key) => {
            this.lpiData.eligibility.eligibilityDetails.add(key, eligibility.eligibilityDetails[key]);
          });
        }
        // retrieve eligibility details only if we have atleast eligid and lname
        if (lname) {
          //debounce(function () {
          this.registrationApiService.getEligibilityDetails(
            params['prg'],
            eligibility.eligibilityDetails.getPostdataUrl(eligibility.eligibilityDetails),
            params['confnum'],
            true
          ).subscribe(
            (result: EligibilityDetail[]) => {
              // set appt details for every eligibility
              if (!!result.length && result[0].id !== "-1") {
                let exams: Exam[] = [];
                if (!!params.exam) {
                  const examCode = this.getExamCodeFromParam(params.exam);
                  result.forEach((eligibilityDetail: EligibilityDetail) => {
                    if (eligibilityDetail.exam.code.toLowerCase() === examCode.toLowerCase()) {
                      exams.push(eligibilityDetail.exam);
                    }
                  });
                } else if (result.length === 1 || (!!result[0].exam.examSet.id && result.every(e => e.exam.examSet.id === result[0].exam.examSet.id))) {
                  result.forEach((eligibilityDetail: EligibilityDetail) => {
                    exams.push(eligibilityDetail.exam);
                  });
                }

                this.setAppointmentDetails(exams);

                this.selectedExamDetailService.consecutiveDays = result[0].consecutiveDays;
                this.selectedExamDetailService.maxDays = result[0].maxDays > 0 ? result[0].maxDays : 0;

                if (result[0].maxDays == 0) {
                  this.selectedExamDetailService.consecutiveDays = true;
                }

                // if the eligibility has single exam without any eligibility error, the eligibility section should be hidden.
                this.selectedExamDetailService.hideExamSelection = true;
                this.eligibilityDetails = result;
                this.selectedExamDetailService.setEligibilityDetails(result);
                this.selectedExamDetailService.invalidEligibilityRequest = '';
              } else {
                this.selectedExamDetailService.invalidEligibilityRequest = result[0].exam.code;
              }

              return resolve(true);
            });
        } else {
          return resolve(true);
        }
      } else if (['fname', 'lname', 'eligid'].filter((elem) => {
        return this.isClosed(params[elem]);
      }).length > 0) {
        return resolve(true);
      } else {
        return resolve(true);
      }
    });
  }
   
  getAppointmentDetails(bookingId: string, bookingDetails: any): Observable<any> {
    return new Observable((observer: any) => {
      bookingDetails.tests.forEach((test: any) => {
        let tempAppointmentDetail: any = {};
        tempAppointmentDetail.status = test.status;
        tempAppointmentDetail.exam = test.exam;
        tempAppointmentDetail.ConfirmationNumber = test.sessions[0].confirmationNumber;
        tempAppointmentDetail.id = test.sessions[0].id;
        tempAppointmentDetail.payments = [];
        tempAppointmentDetail.program = test.program;
        tempAppointmentDetail.candidate = bookingDetails.candidate;
        tempAppointmentDetail.holdId = test.sessions[0].hold.id;

        this.selectedExamDetailService.setProgram(test.program);

        this.schedulingApiService.getAppointmentSlot(test.sessions[0].hold.href).subscribe((appointmentSlot) => {
          tempAppointmentDetail = this.dateTimeService.setAppointmentSlotTimes(appointmentSlot, 0, tempAppointmentDetail, this.selectedExamDetailService.timeFormat);
          
          tempAppointmentDetail.selected = false;
          tempAppointmentDetail.exam.duration = appointmentSlot.duration;

          this.selectedExamDetailService.setScheduledSlot(appointmentSlot);

          if (bookingDetails.payments.length) {
            this.schedulingApiService.getAppointmentDetails(bookingId).subscribe(paymentDetails => {
              paymentDetails.forEach(payment => {
                if (payment.examName.trim() == tempAppointmentDetail.exam.name.trim()) {
                  tempAppointmentDetail.payments.push(payment);
                  const appointmentData = [];
                    appointmentData.push(tempAppointmentDetail);
                   
                  //this.selectedExamDetailService.SetAppointmentDetail(appointmentData);
                  //this.selectedExamDetailService.setSelectedAppointmentDetails(appointmentData);
                  if (constants.voucherPaymentCodes.indexOf(payment.paymentType.toLowerCase()) !== -1) {
                    this.appointmentService.validateVoucherPayment
                      (appointmentData, bookingDetails.candidate, this.overLapStartDate, this.overLapEndDate).subscribe((isAllowed: boolean) => {
                        // TODO: Implement error handling in LPI
                        // this.isRescheduleAllowed = isAllowed;
                      });
                  }
                }
              });
                this.TempAppointmentDetails.push(tempAppointmentDetail);
                this.selectedExamDetailService.SetAppointmentDetail(this.TempAppointmentDetails);
                this.selectedExamDetailService.setSelectedAppointmentDetails(this.TempAppointmentDetails);
              observer.next();
              observer.complete();
            });
          } else {
            const appointmentData = [];
              appointmentData.push(tempAppointmentDetail);
              this.TempAppointmentDetails.push(tempAppointmentDetail);
              this.selectedExamDetailService.SetAppointmentDetail(this.TempAppointmentDetails);
              this.selectedExamDetailService.setSelectedAppointmentDetails(this.TempAppointmentDetails);
            observer.next();
            observer.complete();
          }

        });
      });
    });
  }

  validateConfNumber(params: any) {
    const confNumber = params.confnum;
    let lastName = this.bootstrapParams.candidateLastName || params.lname;
    lastName = lastName && lastName.length > 4 ? lastName.substring(0, 4) : lastName;

    const appointmentSearch = {
      confirmationNumber: confNumber,
      lastName: lastName,
    }
    this.selectedExamDetailService.setAppointmentSearch(appointmentSearch);
    this.selectedExamDetailService.isSearchPerformed = true;
    this.selectedExamDetailService.currentTargetFlow = constants.TargetFlow.AppointmentLookup;

    return new Promise((resolve, reject) => {
      if (!params.confnum || !lastName) return resolve(true);
      if ((/^\d+$/.test(confNumber) && confNumber.length === 16) && lastName !== '') {
        this.schedulingApiService.getBookingDetails(appointmentSearch.confirmationNumber, appointmentSearch.lastName).subscribe(result => {
          const bookingDetails = result;
          if (!!bookingDetails && bookingDetails.id !== "-1") {
            this.selectedExamDetailService.setAppointmentSearch(appointmentSearch);
            this.selectedExamDetailService.setBooking(bookingDetails);
            this.selectedExamDetailService.isSearchPerformed = true;
            this.selectedExamDetailService.currentTargetFlow = constants.TargetFlow.AppointmentLookup;

            // functionality to skip lookup
            // Get eligibility details and then lookup config as
            // incorrect eligibility would not allow to skip page
            this.getEligbilityDetails(bookingDetails, params).subscribe(() => {
              if (!!this.selectedExamDetailService.invalidEligibilityRequest) return resolve(true);
              this.getAppointmentDetails(bookingDetails.id, bookingDetails).subscribe(() => {
                this.selectedExamDetailService.SetCandidateDataSource(bookingDetails.candidate);
                this.selectedExamDetailService.setClient(bookingDetails.client);
                let skipApptLookup = true;
                let skipReschedule = false;
                let cancelNotAllowed = false;
                const configTasks$: Observable<any>[] = [];

                bookingDetails.tests.forEach((test: any) => {
                  //check the bookings response for the reschedule policy, prevent skip if the policy is violated
                  let reschedPolicy = test.sessions[0].canReschedule;
                  if (
                    !reschedPolicy &&
                    (
                      params.path === constants.Path.ProctorReschedule ||
                      params.path === constants.Path.Reschedule
                    )
                  ) {
                    skipApptLookup = false;
                    this.selectedExamDetailService.skipAppointmentLookup = skipApptLookup;
                    return resolve(true);
                  }
                  this.getConsents(test.sessions[0].id);

                  configTasks$.push(this.orchestrationApiService.getConfiguration(
                    [
                      constants.configSettings.SkipAppointmentLookup,
                      constants.configSettings.SkipReschedule,
                      constants.configSettings.CancelNotAllowed,
                      constants.configSettings.SkipMetrics
                    ],
                    bookingDetails.client.code,
                    this.program.code,
                    test.exam.code
                  ));
                });

                forkJoin(configTasks$).subscribe(resp => {
                  if (resp && resp.length) {

                    skipApptLookup = resp.map(r => r[constants.configSettings.SkipAppointmentLookup]).every(function (v) { return v === 'True'; });
                    skipReschedule = resp.map(r => r[constants.configSettings.SkipReschedule]).every(function (v) { return v === 'True'; });
                    cancelNotAllowed = resp.map(r => r[constants.configSettings.CancelNotAllowed]).every(function (v) { return v === 'True'; });
                    this.selectedExamDetailService.skipMetrics = resp.map(r => r[constants.configSettings.SkipMetrics]).every(function (v) { return v === 'True'; });
                    this.selectedExamDetailService.skipAppointmentLookup = skipApptLookup;
                    this.selectedExamDetailService.setSkipReschedule(skipReschedule);
                    this.selectedExamDetailService.setCancelNotAllowed(cancelNotAllowed);
                  }
                  return resolve(true);
                });
              });
            });
          }
        });
      }
    });
  }

  // get eligibility details using booking information
  getEligbilityDetails(bookingDetails: any, params: any) {
    return new Observable((observer: any) => {
      if (!!bookingDetails.tests[0].eligibility.id) {
        this.validateEligibility({
          'eligid': bookingDetails.tests[0].eligibility.id,
          'fname': bookingDetails.candidate.FirstName,
          'lname': bookingDetails.candidate.LastName,
          'prg': params['prg'],
          'confnum': params['confnum']
        }).then(() => {
          observer.next();
          observer.complete();
        });
      } else {
        observer.next();
        observer.complete();
      }
    });
  }

  getExams() {
    return new Promise((resolve, reject) => {
      if (this.isEligibilityRequired) {
        this.schedulingApiService.getExams(this.program.id).subscribe(
          exams => {
            this.exams = exams;
          });
      }
      return resolve(true);
    });
  }

  checkEligibility() {
    return new Promise((resolve, reject) => {
      this.orchestrationApiService.getFlows(this.client.code, this.program.code).subscribe((data) => {
        //this.selectedExamDetailService.setOrchestrationProgramData(data);
        let eligData = data.filter((d: any) => {
          return d.name === 'Validate Eligibility';
        });

        this.isEligibilityRequired = (eligData && eligData[0].settings.required === 'True' ? true : false);
        this.selectedExamDetailService.isEligibilityRequired = (eligData && eligData[0].settings.required === 'True' ? true : false);
        this.selectedExamDetailService.isEligibilityEnabled = (eligData && eligData[0].settings.enabled === 'True' ? true : false);

        if (!this.isEligibilityRequired) {
          this.getExams();
        }

        //Eligibility
        let eligFormResp = data.filter(function (el: any) {
          return el.name == "Validate Eligibility";
        });
        let dynamicFormBase: DynamicFormBase<any>[] = [];
        let apiresponse = eligFormResp[0].data;
        apiresponse.forEach((element: any, index: any) => {
          // need to remove the below code once we are getting respective data from API.
          let order: number = index + 1;
          let validationMessage: string = (element.parameters.display != undefined) ? "Please enter a valid " + element.parameters.display : "";
          let maxLength: number = 255;
          let pattern: string = ".*";

          dynamicFormBase.push(new ProSchedulerTextbox({
            key: element.name,
            label: element.parameters.display,
            required: element.parameters.required,
            order: order,
            validationMessage: validationMessage,
            pattern: pattern,
            maxlength: (element.parameters.length) ? element.parameters.length : maxLength
          }));
        });

        dynamicFormBase = dynamicFormBase.sort((a, b) => a.order - b.order)
        this.selectedExamDetailService.setEligFormBase(dynamicFormBase);

        return resolve(true);

      });
    });
  }

  skipPrivacyPage(params: any) {
    const nopri = (params.nopri === 'T' || params.nopri === 't');
    return new Promise((resolve, reject) => {
      this.selectedExamDetailService.skipPrivacyPage = nopri;
      return resolve(true);
    })
  }

  terminationUrl(params: any) {
    const terminationUrl = params.termurl;
    return new Promise((resolve, reject) => {
      this.selectedExamDetailService.terminationUrl = terminationUrl;
      if (this.checkForTermUrl(params)) return resolve(false);
      return resolve(true);
    })
  }

  skipConfirmationPage(params: any) {
    const skipConfirmPage = (params.noconfirm === 'T' || params.noconfirm === 't');
    return new Promise((resolve, reject) => {

      this.selectedExamDetailService.skipConfirmPage = skipConfirmPage;
      if (this.checkForTermUrl(params)) return resolve(false);
      return resolve(true);
    });
  }

  isClosed(value: any) {
    return value;
  }

  getPath(path: string) {
    const getRoute = this.path[path];
    return this[getRoute].apply(this);
  }

  getSchedulePath() {
    //const path = this.isEligibilityRequired ? '/scheduling/testInformation' : 'scheduling/scheduleappointment';
    const path = this.program ? '/scheduling/testInformation' : '/scheduling/scheduleappointment';
    return path;
  }

  getConfirmPath() {
    this.selectedExamDetailService.showReschedule = false;
    this.selectedExamDetailService.showCancel = false;
    this.selectedExamDetailService.currentTargetFlow = constants.TargetFlow.AppointmentLookup;
    return '/scheduling/appointmentsearch';
  }

  getReschdCancelPath() {
    this.selectedExamDetailService.showReschedule = true;
    this.selectedExamDetailService.showCancel = true;
    this.selectedExamDetailService.currentTargetFlow = constants.TargetFlow.AppointmentLookup;
    return '/scheduling/appointmentsearch';
  }

  getReschdPath() {
    this.selectedExamDetailService.showReschedule = true;
    this.selectedExamDetailService.currentTargetFlow = constants.TargetFlow.Reschedule;

    // if selectedAppointmentDetails is null/empty or if all exams can be rescheduled
    const canResched = (
      !this.selectedAppointmentDetails ||
      !this.selectedAppointmentDetails.length ||
      this.selectedAppointmentDetails.every(appt => this.appointmentService.canScheduleExamInFlow(appt.exam))
      );

    if (this.selectedExamDetailService.skipAppointmentLookup && canResched) {
      return '/scheduling/rescheduleappointment/rescheduleLocation';
    } else {
      this.selectedExamDetailService.setSelectedAppointmentDetails(null);
      return '/scheduling/appointmentsearch';
    }
  }

  getCancelPath() {
    this.selectedExamDetailService.showCancel = true;
    this.selectedExamDetailService.currentTargetFlow = constants.TargetFlow.Cancel;
    return this.selectedExamDetailService.skipAppointmentLookup ? '/scheduling/cancelappointment/verification' : '/scheduling/appointmentsearch';
  }

  getSeatAvail() {
    return '/scheduling/searchAvailability';
  }

  isExamProctor(): boolean {
    return (
      !this.exam ||
      !!this.exam.proctorScheduling
    );
  }

  cleanUpForInvalidRequest() {
    this.selectedExamDetailService.setClient(null);
    this.selectedExamDetailService.setProgram(null);
    this.selectedExamDetailService.setEligibility(null);
    this.selectedExamDetailService.SetAppointmentDetail(null);
  }

  getProctorSchedPath() {
    this.selectedExamDetailService.isProctored = true;
    let path = this.program ? '/scheduling/testInformation' : '/scheduling/scheduleappointment';
    if (!this.isExamProctor()) {
      path = '/scheduling/invalidRequest'
      this.selectedExamDetailService.invalidEligibilityRequest = 'InvalidModality';
      this.cleanUpForInvalidRequest()
    }
    return path;
  }

  getProctorReschedPath() {
    this.selectedExamDetailService.isProctored = true;
    this.selectedExamDetailService.currentTargetFlow = constants.TargetFlow.Reschedule;


    // if selectedAppointmentDetails is null/empty or if all exams can be rescheduled
    const canResched = (
      !this.selectedAppointmentDetails ||
      !this.selectedAppointmentDetails.length ||
      this.selectedAppointmentDetails.every(appt => this.appointmentService.canScheduleExamInFlow(appt.exam))
    );

    let path: string;
    if (this.selectedExamDetailService.skipAppointmentLookup && canResched) {
      path = '/scheduling/rescheduleappointment/rescheduleTimeZone';
    } else {
      this.selectedExamDetailService.setSelectedAppointmentDetails(null);
      path = '/scheduling/rescheduleappointment/reschedule';
    }

    if (!this.isExamProctor()) {
      path = '/scheduling/invalidRequest';
      this.selectedExamDetailService.invalidEligibilityRequest = 'InvalidModality';
      this.cleanUpForInvalidRequest();
    }
    return path;
  }

  getProctorSeatAvail() {
    this.selectedExamDetailService.isProctored = true;
    let path = '/scheduling/searchAvailability';
    if (!this.isExamProctor()) {
      path = '/scheduling/invalidRequest'
      this.selectedExamDetailService.invalidEligibilityRequest = 'InvalidModality';
      this.cleanUpForInvalidRequest()
    }
    return path;
  }

  getApptConfirmationPath() {
    if (this.sessionLoadError) {
      return '/scheduling/invalidRequest';
    }

    this.selectedExamDetailService.isCompleteApptPath = true;
    this.selectedExamDetailService.isPaymentConfirmed = true;

    switch (this.selectedExamDetailService.currentTargetFlow) {
      case constants.TargetFlow.Schedule:
        return '/scheduling/confirmation';
      case constants.TargetFlow.Reschedule:
        return '/scheduling/rescheduleappointment/reconfirmation';
      case constants.TargetFlow.Cancel:
        return '/scheduling/cancelappointment/confirmation';
      default:
        return '/scheduling/invalidRequest';
    }
  }

  setPathLock(params: any) {
    if (params.pl === 'true') {
      this.selectedExamDetailService.setPathLock(true);

      // TODO: update with constants for Path
      switch (params.path) {
        case 'schd':
          this.selectedExamDetailService.setSchedulePathLock = true;
          if (this.program) {
            this.selectedExamDetailService.pathLockTestInformation = true;
          }
          break;
        case 'reschd':
          this.selectedExamDetailService.setReschedulePathLock = true;
          break;
        case 'cancel':
          this.selectedExamDetailService.setCancelPathLock = true;
          break;
        case 'reschdcancel':
          this.selectedExamDetailService.setReschedCancelPathLock = true;
          this.selectedExamDetailService.setCancelPathLock = true;
          this.selectedExamDetailService.setReschedulePathLock = true;
          break;
        case 'seatavail':
          this.selectedExamDetailService.setSeatAvailabilityPathLock = true;
          break;
        case 'proctorsch':
          this.selectedExamDetailService.setSchedulePathLock = true;
          if (this.program) {
            this.selectedExamDetailService.pathLockTestInformation = true;
          }
          break;
        case 'proctorresch':
          this.selectedExamDetailService.setReschedulePathLock = true;
          break;
        case 'proctorseatavail':
          this.selectedExamDetailService.setSeatAvailabilityPathLock = true;
          break;
        default: // confirm
          this.selectedExamDetailService.setConfirmPathLock = true;
          this.selectedExamDetailService.setCancelPathLock = true;
          this.selectedExamDetailService.setReschedulePathLock = true;
          break;
      }
    }
    return true;
  }

  setCandidateDemographics(params: any) {
    return new Promise((resolve, reject) => {
      if (!params.candDemo) {
        return resolve(true);
      }

      let selectedCandidate = this.lpiCandidate;

      ['address1', 'address2', 'address3', 'address4'].forEach(a => {
        if (selectedCandidate.defaultDetails.hasOwnProperty(a) && !!selectedCandidate.defaultDetails[a]) {
          selectedCandidate.defaultDetails[a] = selectedCandidate.defaultDetails[a].toString().replace(/\+/g, ' ');
        }
      });

      if ((selectedCandidate.defaultDetails.hasOwnProperty('country') && !!selectedCandidate.defaultDetails['country']) ||
        (selectedCandidate.defaultDetails.hasOwnProperty('gov_id_country') && !!selectedCandidate.defaultDetails['gov_id_country']) ||
        (selectedCandidate.defaultDetails.hasOwnProperty('country_of_birth') && !!selectedCandidate.defaultDetails['country_of_birth'])
      ) {

        ['country', 'gov_id_country', 'country_of_birth'].forEach(c => {
          let countryCode = selectedCandidate.defaultDetails[c];
          this.schedulingApiService.getCountries().subscribe(
            countries => {
              this.selectedExamDetailService.setCountries(countries);
              let country = countries.filter(tempCountry => tempCountry.id.toLowerCase() === countryCode.toLowerCase());

              if (country && country.length) {
                selectedCandidate.defaultDetails[c] = country[0].name;

                // prepopulate state
                let stateCode = selectedCandidate.defaultDetails['state'] ? selectedCandidate.defaultDetails['state'].toString() : '';
                if (stateCode.length) {
                  this.schedulingApiService.getProvinces(country[0].id).subscribe(
                    regions => {
                      if (stateCode.length < 3 && regions) {
                        let tempRegion = regions.filter(tempState => tempState.id.toLowerCase() == stateCode.toLowerCase());

                        if (tempRegion.length) {
                          selectedCandidate.defaultDetails['state'] = tempRegion[0].name;
                        }
                      } else if (stateCode.length > 3 && regions) {
                        let tempRegion = regions.filter(tempState => tempState.name.toLowerCase() == stateCode.toLowerCase());

                        if (tempRegion.length) {
                          selectedCandidate.defaultDetails['state'] = tempRegion[0].name;
                        }
                      }
                    });
                }
              }
            });
        });
      }

      this.selectedExamDetailService.setLpiCandidateSource(selectedCandidate);
      return resolve(true);
    });
  }

  decodeHtmlEntity(htmlProperty: any): string {
    return decodeURIComponent(htmlProperty);
  }

  checkForTermUrl(params: any) {
    const noconfirm = params.noconfirm ? (params.noconfirm === 't' || params.noconfirm === 'T') : false;
    const termUrl = params.termurl;
    this.selectedExamDetailService.errorOnHomePage = noconfirm && !termUrl;
    return this.selectedExamDetailService.errorOnHomePage;
  }

  setCountryCode(params: any) {
    return new Promise((resolve, reject) => {
      if (!params.cntry) {
        return resolve(true);
      }
      const countryCode = params.cntry;
      const stateCode = params.st
      this.schedulingApiService.getCountries().subscribe(
        countries => {
          if (!!countryCode && countryCode.toString().length < 4) {
            if (!!countryCode) {
              let filteredCountries: Country[] = countries.filter(tempCountry => tempCountry.id.toLowerCase() == countryCode.toString().toLowerCase());
              // Check whether Country is valid or not
              if (filteredCountries != null && filteredCountries != undefined && filteredCountries.length) {
                this.selectedExamDetailService.setLpiCountry(filteredCountries[0]);

                if (stateCode) {
                  this.schedulingApiService.getProvinces(filteredCountries[0].id).subscribe(
                    (regions) => {
                      let inputItems = regions;
                      if (stateCode.toString().length <= 3 && inputItems && inputItems.length) {
                        let tmpRegion: any = inputItems.filter(tempState => tempState.id.toLowerCase() === stateCode.toString().toLowerCase());
                        if (tmpRegion.length) {
                          this.selectedExamDetailService.setLpiState(tmpRegion[0]);
                        }
                      }
                    }
                  )
                }
              }
            }
          } else if (!!countryCode && countryCode.toString().length > 3) {
            if (!!countryCode) {
              let filteredCountries: Country[] = countries.filter(tempCountry => tempCountry.name.toLowerCase() === countryCode.toString().toLowerCase());
              // Check whether Country is valid or not
              if (filteredCountries != null && filteredCountries != undefined && filteredCountries.length) {
                this.selectedExamDetailService.setLpiCountry(filteredCountries[0]);

                if (stateCode) {
                  this.schedulingApiService.getProvinces(filteredCountries[0].id).subscribe(
                    (regions) => {
                      let inputItems = regions;
                      if (stateCode.toString().length <= 3 && inputItems && inputItems.length) {
                        let tmpRegion: any = inputItems.filter(tempState => tempState.id.toLowerCase() === stateCode.toString().toLowerCase());
                        if (tmpRegion.length) {
                          this.selectedExamDetailService.setLpiState(tmpRegion[0]);
                        }
                      }
                    }
                  )
                }
              }
            }
          }
          return resolve(true);
        }
      );
    });
  }

  setStateCode(params: any) {
    return new Promise((resolve, reject) => {
      if (!params.st || !params.cntry) {
        return resolve(true);
      }

      const countryCode = params.cntry;
      const stateCode = params.st
      this.schedulingApiService.getCountries().subscribe(
        countries => {
          if (!!countryCode && countryCode.length < 4) {
            if (countryCode != null && countryCode != undefined && countryCode != "") {
              let filteredCountries: Country[] = countries.filter(tempCountry => tempCountry.id.toLowerCase() == countryCode);
              // Check whether Country is valid or not
              if (filteredCountries != null && filteredCountries != undefined && filteredCountries.length) {
                this.selectedExamDetailService.setLpiCountry(filteredCountries[0]);

                if (stateCode) {
                  this.schedulingApiService.getProvinces(filteredCountries[0].id).subscribe(
                    (regions) => {
                      let inputItems = regions;
                      if (stateCode.length <= 3 && inputItems && inputItems.length) {
                        let tmpRegion: any = inputItems.filter(tempState => tempState.id.toLowerCase() == stateCode.toLowerCase());
                        if (tmpRegion.length) {
                          this.selectedExamDetailService.setLpiState(tmpRegion[0]);
                        }
                      }
                    }
                  )
                }
              }
            }
          } else if (!!countryCode && countryCode.length > 3) {
            if (countryCode != null && countryCode != undefined && countryCode != "") {
              let filteredCountries: Country[] = countries.filter(tempCountry => tempCountry.name.toLowerCase() == countryCode.toLowerCase());
              // Check whether Country is valid or not
              if (filteredCountries != null && filteredCountries != undefined && filteredCountries.length) {
                this.selectedExamDetailService.setLpiCountry(filteredCountries[0]);

                if (stateCode) {
                  this.schedulingApiService.getProvinces(filteredCountries[0].id).subscribe(
                    (regions) => {
                      let inputItems = regions;
                      if (stateCode.length <= 3 && inputItems && inputItems.length) {
                        let tmpRegion: any = inputItems.filter(tempState => tempState.id.toLowerCase() == stateCode.toLowerCase());
                        if (tmpRegion.length) {
                          this.selectedExamDetailService.setLpiState(tmpRegion[0]);
                        }
                      }
                    }
                  )
                }
              }
            }
          }
          return resolve(true);
        }
      );

    });
  }

  setCulture(params: any) {
    const culture: string = params.culture;
    return new Promise((resolve, reject) => {
      this.selectedExamDetailService.culture = culture;
      if (culture) {
        let selectedLanguage: any;
        selectedLanguage = constants.LanguageCultureCodes.filter(elm => elm.culture.toLowerCase() === culture.toLowerCase());

        if (selectedLanguage.length) {
          this.selectedExamDetailService.setLanguageCode(selectedLanguage[0].key);
          this.selectedExamDetailService.setResourceCulture(selectedLanguage[0].resourceFileCulture);
          this.selectedExamDetailService.selectedLanguage = selectedLanguage[0].lang;
          this.selectedExamDetailService.defaultLanguageCode = 'en';
        }
      }
      return resolve(true);
    })
  }

  getSessionData(params: any) {
    const sessionId: string = params.session;
    return new Promise((resolve, reject) => {
      if (!!sessionId) {
        const session = this.selectedExamDetailService.loadFromLocalStorage(sessionId);
        this.sessionLoadError = !!session.error;
        this.selectedExamDetailService.isSessionLoadError = this.sessionLoadError;
        if (this.sessionLoadError) {
          let logMessage = !!session.exception ? session.error + ` Error: ${JSON.stringify(session.exception)}` : session.error;
          this.metricApiService.log(LogLevel.Debug, logMessage);
        }
        else {
          // Update contextId & bearer token (if exists) to relate previous session
          this.bootstrapParams.sessionGuid = session.sessionId;
          this.bootstrapParams.Token = this.selectedExamDetailService.ssoToken;
        }
      }
      return resolve(true);
    });
  }

  /* Main function where the promiseChain of the steps are created */
  processParams(params: any) {
    // get array of parameters in a particular path
    const pathParameters = this.pathParameters[params.path] || [];
    if (!pathParameters.length) return Promise.resolve(false);
    //replace plus signs where there should be spaces for lname validation
    if (params.lname != null && params.lname.indexOf("+") > 0) {
      params.lname = params.lname.replace(/\++/g, " ");
    }
    pathParameters.forEach((param: any) => {
      if (param == 'lname') return;
      this.workflow[param].forEach((step: string) => {
        // invoke each function mapped to required parameters
        this.promiseChain.push(this[step].apply(this, [params]));
      });
    });
    // resolve all promises and navigate to the route
    return Promise.all(this.promiseChain)
      .then((values) => {
        if (values.every(value => this.isClosed(value))) {

          if (this.client && this.program && this.exam && !params.eligid && (params.path === constants.Path.Schedule || params.path === constants.Path.ProctorSchedule)) {
            this.orchestrationApiService.getFlows(this.client.code, this.program.code, this.exam.code).subscribe((response: any) => {
              if (response.length > 0) {
                response = response.filter(function (el: any) {
                  return el.name == "Candidate Selectable";
                });
                // check if testingAccommodations have length
                this.selectedExamDetailService.showCandidateSelectableView =
                  response.length ? !!response[0].data.length : false;
              }
            });
          } else if (this.client && this.program && (params.path === constants.Path.Schedule || params.path === constants.Path.ProctorSchedule)) {
            this.orchestrationApiService.getFlows(this.client.code, this.program.code).subscribe((data) => {
              let eligData = data.filter((d: any) => {
                return d.name === 'Validate Eligibility';
              });
              // required flag indicating whether exam is eligibility driven and required
              this.isEligibilityRequired = (eligData && eligData[0].settings.required === 'True' ? true : false);
              this.selectedExamDetailService.isEligibilityRequired = this.isEligibilityRequired;
              this.selectedExamDetailService.isEligibilityEnabled = (eligData && eligData[0].settings.enabled === 'True' ? true : false);

              // skip ExamSelection page if eligibility is required and has only one exam
              this.selectedExamDetailService.skipExamSelection = this.selectedExamDetailService.hideExamSelection
                && this.isEligibilityRequired && this.eligibilityDetails.length === 1;

              //Eligibility
              let dynamicFormBase: DynamicFormBase<any>[] = [];
              let apiresponse = eligData[0].data;
              apiresponse.forEach((element: GetFlowsResponseItemData, index: any) => {
                // need to remove the below code once we are getting respective data from API.
                let order: number = index + 1;
                let validationMessage: string = (element.parameters.display != undefined) ? "Please enter a valid " + element.parameters.display : "";
                let maxLength: number = 255;
                let pattern: string = ".*";

                // update eligibility details based on flows parameter restrictions
                this.updateEligibilityDetailsByElement(element);

                dynamicFormBase.push(new ProSchedulerTextbox({
                  key: element.name,
                  label: element.parameters.display,
                  required: element.parameters.required,
                  order: order,
                  validationMessage: validationMessage,
                  pattern: pattern,
                  maxlength: (element.parameters.length) ? element.parameters.length : maxLength
                }));
              });

              dynamicFormBase = dynamicFormBase.sort((a, b) => a.order - b.order)
              this.selectedExamDetailService.setEligFormBase(dynamicFormBase);
            });
          }

          if (this.selectedExamDetailService.skipAppointmentLookup) {
            this.selectedExamDetailService.skipAppointmentLookup = this.selectedExamDetailService.skipAppointmentLookup && params.path !== 'reschdcancel';
          }

          this.router.navigate([this.getPath(params.path)]);
        }
        return true;
      });
  }

  getClientDetails(href: string): Observable<Client[]> {
    return new Observable((observer: Observer<Client[]>) => {
      this.httpService.get(href + '?isPublished=true', this.bootstrapParams.Token)
        .subscribe((rArray) => {
          observer.next(rArray);
          observer.complete();
        });
    });
  }

  /**
   * Based on api flows, update eligibility details
   * @param element api/v1/flows response object
   */
  updateEligibilityDetailsByElement(element: GetFlowsResponseItemData) {

    // LAST_NAME: concatenate eligibility last name based on parameters['length']
    const eligibilityLastNameKey: string = 'LAST_NAME';
    if (element.name === eligibilityLastNameKey && element.parameters && element.parameters.length) {
      if (this.lpiData.eligibility && this.lpiData.eligibility.eligibilityDetails && this.lpiData.eligibility.eligibilityDetails.containsKey(eligibilityLastNameKey)) {
        this.lpiData.eligibility.eligibilityDetails[eligibilityLastNameKey] = this.lpiData.eligibility.eligibilityDetails[eligibilityLastNameKey].substring(0, element.parameters.length);
        this.selectedExamDetailService.setLpiData(this.lpiData);
      }
    }
  }

  setAppointmentDetails(exams: Exam[]) {
    const selectedAppointments: AppointmentDetail[] = [];

    exams.forEach(exam => {
      let tempAppointmentDetail: AppointmentDetail = new AppointmentDetail();
      tempAppointmentDetail.exam = exam;
      tempAppointmentDetail.program = this.program;
      tempAppointmentDetail.examName = exam.name;
      tempAppointmentDetail.client = this.client;
      tempAppointmentDetail.appointmentSlot = new AppointmentSlot();
      tempAppointmentDetail.appointmentSlot.site = new SiteSlot(null, null, null, null, null, null);
      tempAppointmentDetail.payments = [];
      let payment: Payment = new Payment(0, null, Fee.ExamFee);
      tempAppointmentDetail.payments.push(payment);
      selectedAppointments.push(tempAppointmentDetail);
    });

    if (exams.length === 1) {
      this.selectedExamDetailService.selectedAppointmentIndex = 0;
      this.selectedExamDetailService.setSelectedAppointmentDetails(selectedAppointments);
      this.selectedExamDetailService.appointmentCountForInfo.next(0);
      this.selectedExamDetailService.setOverlap(selectedAppointments);
    }
  }

  getConsents(confirmationId: any) {
    this.schedulingApiService.getConsents(confirmationId).subscribe((resp) => {
      if (!!resp.Results[0].Consent) {
        let consent = resp.Results[0].Consent;
        if (!!consent.biometric && consent.biometric.length > 0) {
          if (consent.biometric[0] === 'True') {
            this.selectedExamDetailService.isBiometricConsentSavedInSchedule = true;
          } else {
            this.selectedExamDetailService.isBiometricConsentSavedInSchedule = false;
          }
        }
      }
    });
  }
}
